import { Alert, Button, Card, Spinner, Table } from "flowbite-react";
import React, { useEffect, useRef, useState } from "react";
import {
  formatDateToYY,
  formatDocument,
  formatToReal,
  isValidCNPJ,
  maskCEP,
} from "../../utils/utils";
import axios from "../../config/axiosInstance";
import { QUERIES } from "../../config/apiConfig";
import { HiInformationCircle } from "react-icons/hi";
import HeaderQuery from "./HeaderQuery";
import TitleStripe from "../common/TitleStripe";
import CardQueryDescription from "../common/CardQueryDescription";
import QueryInput from "../inputs/QueryInput";
import SliderToPreview from "../common/SliderToPreview";
import QueryLoading from "../common/QueryLoading";
import AvisoCard from "../common/query/AvisoCard";
import PendenciasFinanceiras from "../common/query/PendenciasFinanceiras";
import NoOcurrence from "../common/query/NoOcurrence";
import Protestos from "../common/query/Protestos";
import ProtestosDirCartorio from "../common/query/ProtestosDirCartorio";
import ChequesSustados from "../common/query/ChequesSustados";
import ChequesSemFundos from "../common/query/ChequesSemFundos";
import HistoricoConsultas from "../common/query/HistoricoConsultas";
import { EvaluationScorePJ } from "../common/query/EvaluationScorePJ";
import DadosReceitaFederalPJ from "../common/query/DadosReceitaFederalPJ";
import RatingCredito from "../common/query/RatingCredito";
import FontesSemRetorno, {
  fonteDisponivel,
} from "../common/query/FontesSemRetorno";
import FooterQuery from "./FooterQuery";
import ReactApexChart from "react-apexcharts";
import moment from "moment";
import { useReactToPrint } from "react-to-print";
import { FaPrint } from "react-icons/fa";
import { LuMapPin } from "react-icons/lu";

const calculateCompanyAgeWithMoment = (dateStr) => {
  if (!dateStr) return null;

  // tenta vários formatos comuns
  const foundation = moment(
    dateStr,
    ["DD/MM/YYYY", "YYYY-MM-DD", "YYYYMMDD"],
    true
  );
  if (!foundation.isValid()) return null;

  const now = moment();

  const years = now.diff(foundation, "years");
  const afterYears = foundation.clone().add(years, "years");
  const months = now.diff(afterYears, "months");

  if (years < 0) return null;

  const anosLabel = years > 0 ? `${years} ano${years > 1 ? "s" : ""}` : null;
  const mesesLabel =
    months > 0 ? `${months} mês${months > 1 ? "es" : ""}` : null;

  if (anosLabel && mesesLabel)
    return `${anosLabel} e ${mesesLabel} de atividade`;
  if (anosLabel) return `${anosLabel} de atividade`;
  if (mesesLabel) return `${mesesLabel} de atividade`;

  return "menos de 1 mês de atividade";
};

const formatYYYYMMToYY = (yyyyMM) => {
  if (!yyyyMM) return "–";
  const [y, m] = yyyyMM.split("-");
  return `${m}/${String(y).slice(-2)}`;
};

// A Boa Vista manda a linha "Pontuação" do indicador junto com os meses, e ela
// chega com mes nulo.
const formatMesOuPontuacao = (mes) =>
  mes ? formatYYYYMMToYY(mes) : "Pontuação";

const FAIXAS_ATRASO = [
  ["Período 6 a 15 dias", "dias_6_15"],
  ["Período 16 a 30 dias", "dias_16_30"],
  ["Período 31 a 60 dias", "dias_31_60"],
  ["Mais de 60 dias", "mais_60"],
  ["Atraso médio (dias)", "atraso_medio_dias"],
];

// Fontes que a Relinq devolve nesta consulta, cada uma com o seu statusRetorno.
const FONTES_CONSULTA = {
  defineRisco: "Define Risco",
  receitaFederalPJ: "Receita Federal",
  spcTerceirosPJ: "SPC / Serasa",
  scoreRating: "Rating de crédito",
};

const formatPeriodoLabel = (periodo) => {
  if (!periodo) return "–";
  if (typeof periodo === "string" && periodo.includes("-")) {
    return formatYYYYMMToYY(periodo);
  }
  return periodo;
};

const getPeriodoFromEntry = (entry) =>
  entry?.periodo ?? entry?.mes ?? entry?.mes_ano ?? entry?.periodo_consulta;

const PositivoPlusCNPJQuery = ({
  query,
  selectedPrice,
  dataFromLocalStorage = null,
  hasHeaderPDF = "true",
  // Quem embute este laudo (Completa Plus CNPJ) passa o próprio ref de impressão.
  printRef = null,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [info, setInfo] = useState("");
  const [document, setDocument] = useState("");
  const [error, setError] = useState("");
  const [errorData, setErrorData] = useState("");
  const [data, setData] = useState(null);
  // Resposta inteira: `data` é só o Define Risco, as demais fontes vêm ao lado.
  const [modulos, setModulos] = useState(null);
  const [isLoadingProtestos, setIsLoadingProtestos] = useState(false);
  const [infoProtestos, setInfoProtestos] = useState(null);
  const [isDoneRequest, setIsDoneRequest] = useState(false);
  const localRef = useRef();
  const componentRef = printRef || localRef;
  const reactToPrintFn = useReactToPrint({ contentRef: componentRef });

  useEffect(() => {
    if (dataFromLocalStorage) {
      setData(dataFromLocalStorage.defineRisco?.consultaCredito);
      setModulos(dataFromLocalStorage);
      setDocument(
        dataFromLocalStorage?.defineRisco?.consultaCredito?.dadosCadastrais
          ?.cnpj
      );
      setIsDoneRequest(true);
    }
  }, []);

  const fetchDefineRisco = async (raw) => {
    return await axios(
      `${QUERIES.GET_V2_QUERY}/essencial-plus-cnpj/${info.replace(/\D/g, "")}`
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setDocument(info);

    const unmaskedValue = info.replace(/\D/g, "");
    let isValid = true;

    if (!isValidCNPJ(unmaskedValue)) {
      setError("CNPJ inválido!");
      isValid = false;
    }

    if (isValid) {
      try {
        setData(null);
        setModulos(null);
        setIsLoading(true);
        setError("");
        setErrorData("");
        setIsDoneRequest(false);

        const { data: dados } = await fetchDefineRisco();
        setData(dados?.data?.defineRisco?.consultaCredito);
        setModulos(dados?.data);
        setIsLoading(false);
      } catch (error) {
        setErrorData(
          typeof error?.response?.data?.message !== "object"
            ? error?.response?.data?.message
            : "Algum erro ocorreu com sua consulta"
        );
        setIsLoading(false);
      } finally {
        setIsDoneRequest(true);
      }
    } else {
      setData(null);
    }
  };

  const companyAgeLabel = calculateCompanyAgeWithMoment(
    data?.dadosCadastrais?.dataFundacao
  );

  const statusConsumidor = data?.statusConsumidor;
  const statusConsumidorContainsNao = statusConsumidor
    ? statusConsumidor.toUpperCase().includes("NÃO") ||
      statusConsumidor.toUpperCase().includes("CANCELADA") ||
      statusConsumidor.toUpperCase().includes("NAO") ||
      statusConsumidor.toUpperCase().includes("SEM")
    : false;

  const compromissosSeries = data?.comprometimentoFuturo?.compromissos ?? [];
  const creditosSeries = data?.comprometimentoFuturo?.credito ?? [];
  const periodosSeries = [
    ...new Set(
      [
        ...compromissosSeries.map((pt) => pt.periodo).filter(Boolean),
        ...creditosSeries.map((pt) => pt.periodo).filter(Boolean),
      ].filter(Boolean)
    ),
  ];
  const getCompromissoSerie = (periodo) =>
    compromissosSeries.find((item) => item.periodo === periodo);
  const getCreditoSerie = (periodo) =>
    creditosSeries.find((item) => item.periodo === periodo);
  const formatCompromissoValor = (valor) => {
    const parsed = parseFloat(valor);
    if (isNaN(parsed)) {
      return "–";
    }
    if (parsed === 0) {
      return "< 10";
    }
    return parsed * 100;
  };
  const formatCreditoValor = (valor) => {
    const parsed = parseFloat(valor);
    return !isNaN(parsed) ? formatToReal(parsed * 1000) : "–";
  };
  const creditoObtidoSeries = data?.creditoObtido?.series ?? [];
  const compromissosOperacoesSeries = data?.compromissos?.series ?? [];
  const periodosOperacoes = [
    ...new Set(
      [
        ...creditoObtidoSeries
          .map((item) => getPeriodoFromEntry(item))
          .filter(Boolean),
        ...compromissosOperacoesSeries
          .map((item) => getPeriodoFromEntry(item))
          .filter(Boolean),
      ].filter(Boolean)
    ),
  ];
  const getCreditoObtidoPorPeriodo = (periodo) =>
    creditoObtidoSeries.find((item) => getPeriodoFromEntry(item) === periodo);
  const getCompromissosPorPeriodo = (periodo) =>
    compromissosOperacoesSeries.find(
      (item) => getPeriodoFromEntry(item) === periodo
    );
  const getValorTitulosPorPeriodo = (periodo) => {
    const creditoValor = getCreditoObtidoPorPeriodo(periodo)?.valor;
    if (creditoValor != null && creditoValor !== "") {
      return creditoValor;
    }
    return getCompromissosPorPeriodo(periodo)?.valor;
  };
  const formatCreditoObtidoValor = (valor) => {
    const parsed = parseFloat(valor);
    return !isNaN(parsed) ? formatToReal(parsed * 1000) : "–";
  };
  // A Boa Vista manda as faixas do comprometimento mesmo sem nenhum valor.
  const temComprometimentoFuturo = [
    ...compromissosSeries,
    ...creditosSeries,
  ].some((pt) => pt?.pontuacao != null);

  // Colunas casadas pelo mês, e não pela posição, com a "Pontuação" primeiro.
  const seriesAtraso = FAIXAS_ATRASO.map(
    ([, key]) => data?.pagamentoAtrasado?.[key]
  ).filter(Array.isArray);
  const mesesAtraso = [
    ...new Set(seriesAtraso.flat().map((pt) => pt?.mes).filter(Boolean)),
  ].sort();
  const colunasAtraso = seriesAtraso.some((serie) =>
    serie.some((pt) => !pt?.mes)
  )
    ? [null, ...mesesAtraso]
    : mesesAtraso;

  const receitaFederal = fonteDisponivel(modulos?.receitaFederalPJ);
  const spcSerasa = fonteDisponivel(modulos?.spcTerceirosPJ);
  const scoreRating = fonteDisponivel(modulos?.scoreRating);

  // No SPC Terceiros o lado Serasa vem em pendenciasFinanceiras e o lado SPC em
  // spc. Lista vazia com total no resumo é pendência informada sem detalhe.
  const pendenciasSerasa =
    spcSerasa?.pendenciasFinanceiras ?? spcSerasa?.serasa ?? [];
  const pendenciasSpc = spcSerasa?.spc ?? spcSerasa?.pendenciasSpc ?? [];
  const quantidadeSerasa =
    pendenciasSerasa.length ||
    +spcSerasa?.resumoConsulta?.serasa?.quantidadeTotal ||
    0;
  const quantidadeSpc =
    pendenciasSpc.length ||
    +spcSerasa?.resumoConsulta?.spc?.quantidadeTotal ||
    0;

  return (
    <div>
      {!dataFromLocalStorage && (
        <>
          <CardQueryDescription
            title={query?.name || "Informações da consulta"}
            infos={[
              "Dados Cadastrais",
              "Score",
              "Negativações",
              "Protestos",
              "Cheques sem fundos",
              "Cheques sustados",
            ]}
            price={selectedPrice}
          >
            <form
              className="flex flex-col gap-2 bg-white p-2 rounded-lg shadow-md w-fit"
              onSubmit={handleSubmit}
            >
              <div className="flex flex-col md:flex-row gap-2 items-center">
                <QueryInput
                  info={info}
                  setInfo={setInfo}
                  isLoading={isLoading}
                  error={error}
                  type="CNPJ"
                />
                <div>
                  {!errorData && data && (
                    <Button
                      className="bg-gray-600"
                      onClick={() => reactToPrintFn()}
                    >
                      <div className="flex gap-2 items-center">
                        <FaPrint />
                        <div>Imprimir</div>
                      </div>
                    </Button>
                  )}
                </div>
              </div>
            </form>
          </CardQueryDescription>
        </>
      )}

      <SliderToPreview />

      {errorData ? (
        <Alert color="failure" icon={HiInformationCircle}>
          <span className="font-medium">{errorData}</span>
        </Alert>
      ) : (
        <>
          <FontesSemRetorno
            modulos={modulos}
            fontes={FONTES_CONSULTA}
            semLaudo={!data}
          />
          <div
            className={`overflow-x-scroll print-content  mobile-fit ${
              data || isLoading ? "block" : "hidden"
            }`}
            ref={componentRef}
          >
            {!isLoading ? (
              <>
                <Card className="mt-0 p-4 card-pdf relative overflow-hidden">
                  <HeaderQuery
                    title={query?.name}
                    hasHeader={hasHeaderPDF}
                    date={modulos?.resumoRetorno?.dataConsulta || ""}
                  />

                  <TitleStripe>Quadro de avisos</TitleStripe>

                  <div
                    className={`container-alerts ${
                      spcSerasa ? "five-columns" : "three-columns"
                    }`}
                  >
                    <AvisoCard
                      title="Negativações"
                      hasRestrictions={
                        data?.resumoConsulta?.pendenciasFinanceiras
                          ?.quantidadeTotal != 0
                      }
                      messageError={`${data?.resumoConsulta?.pendenciasFinanceiras?.quantidadeTotal}
  ocorrências`}
                    />
                    <AvisoCard
                      title="Cheques sem Fundos"
                      hasRestrictions={
                        data?.resumoConsulta?.chequesSemFundo
                          ?.quantidadeTotal != 0
                      }
                      messageError={`${data?.resumoConsulta?.chequesSemFundo?.quantidadeTotal} 
      ocorrências`}
                    />

                    <AvisoCard
                      title="Protestos"
                      hasRestrictions={
                        data?.resumoConsulta?.protestos?.quantidadeTotal != 0
                      }
                      messageError={`${data?.resumoConsulta?.protestos?.quantidadeTotal} ocorrências`}
                    />
                    {spcSerasa && (
                      <>
                        <AvisoCard
                          title="Pendências Serasa"
                          hasRestrictions={quantidadeSerasa > 0}
                          messageError={`${quantidadeSerasa} ocorrências`}
                        />
                        <AvisoCard
                          title="Pendências SPC"
                          hasRestrictions={quantidadeSpc > 0}
                          messageError={`${quantidadeSpc} ocorrências`}
                        />
                      </>
                    )}
                  </div>

                  <TitleStripe>Dados Cadastrais</TitleStripe>
                  <div className="text-xs">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                          Razão Social
                        </p>
                        <p className="text-lg font-semibold text-gray-900">
                          {data?.dadosCadastrais?.razaoSocial || "Indisponível"}
                        </p>
                        <p className="text-[11px] text-gray-500">
                          {data?.dadosCadastrais?.nomeFantasia ||
                            "Sem nome fantasia"}
                        </p>
                      </div>
                    </div>

                    <div className="mt-2 flex flex-wrap gap-2 text-[10px]">
                      <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
                        {data?.dadosCadastrais?.situacao ||
                          "Situação desconhecida"}
                      </span>
                      <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
                        Fundação: {data?.dadosCadastrais?.dataFundacao || "—"}
                      </span>
                      {companyAgeLabel && (
                        <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
                          {companyAgeLabel}
                        </span>
                      )}
                    </div>

                    <div className="mt-3 grid gap-2 grid-cols-2">
                      {[
                        {
                          label: "CNPJ",
                          value: data?.dadosCadastrais?.cnpj
                            ? formatDocument(data?.dadosCadastrais?.cnpj)
                            : "Não disponível",
                          hint: "Documento",
                        },
                        {
                          label: "CNAE",
                          value:
                            data?.dadosCadastrais?.ramoAtividade ||
                            "Não informado",
                          hint: "Atividade",
                        },
                        {
                          label: "Natureza",
                          value:
                            data?.dadosCadastrais?.naturezaJuridica ||
                            "Não informado",
                          hint: "Perfil jurídico",
                        },
                        {
                          label: "Cidade / UF",
                          value: `${
                            data?.dadosCadastrais?.cidade || "Não informado"
                          } / ${data?.dadosCadastrais?.uf || "--"}`,
                          hint: "Localização",
                        },
                      ].map((item) => (
                        <div
                          key={item.label}
                          className="rounded-xl border border-gray-200 bg-slate-50 px-3 py-2"
                        >
                          <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                            {item.label}
                          </p>
                          <p className="text-base font-semibold text-gray-900">
                            {item.value}
                          </p>
                          <p className="text-[10px] text-gray-500">
                            {item.hint}
                          </p>
                        </div>
                      ))}
                    </div>

                    <TitleStripe className="mb-4">
                      <div className="flex items-center gap-2">
                        <LuMapPin className="text-blue-700" />{" "}
                        <span>Localização</span>
                      </div>
                    </TitleStripe>
                    <div className="grid gap-2 grid-cols-2">
                      <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                          Endereço
                        </p>
                        <div className="flex items-center gap-2">
                          <p className="text-[12px] font-semibold text-gray-900">
                            {data?.dadosCadastrais?.endereco || "Não informado"}
                          </p>
                        </div>
                      </div>
                      <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                          Bairro · CEP
                        </p>
                        <p className="text-[12px] font-semibold text-gray-900 flex flex-wrap gap-1">
                          <span>{data?.dadosCadastrais?.bairro || "—"}</span>
                          <span>·</span>
                          <span>
                            {maskCEP(data?.dadosCadastrais?.cep) || "--"}
                          </span>
                        </p>
                      </div>
                    </div>

                    <div className="mt-2 grid gap-2 grid-cols-2">
                      <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                          Cidade
                        </p>
                        <p className="text-[12px] font-semibold text-gray-900">
                          {data?.dadosCadastrais?.cidade || "—"}
                        </p>
                      </div>
                      <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                          UF
                        </p>
                        <p className="text-[12px] font-semibold text-gray-900">
                          {data?.dadosCadastrais?.uf || "--"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {receitaFederal && (
                    <DadosReceitaFederalPJ receita={receitaFederal} />
                  )}

                  <div>
                    <TitleStripe>Avaliação preliminar de crédito</TitleStripe>
                  </div>
                  <EvaluationScorePJ
                    data={data}
                    score={data?.score?.score}
                    probability={+data?.score?.probabilidade * 100}
                    type="CNPJ"
                    hideScore={true}
                    hasIncome={false}
                  />
                  {data?.score?.mensagem && (
                    <p className="mt-2 text-xs text-gray-500">
                      {data.score.mensagem}
                    </p>
                  )}
                  {scoreRating && <RatingCredito rating={scoreRating} />}
                  <div>
                    <TitleStripe className="mb-4">Negativações</TitleStripe>

                    {data?.pendenciasFinanceiras?.length > 0 ? (
                      <>
                        <PendenciasFinanceiras
                          quantidadeTotal={
                            data?.resumoConsulta?.pendenciasFinanceiras
                              ?.quantidadeTotal
                          }
                          valorTotal={
                            data?.resumoConsulta?.pendenciasFinanceiras
                              ?.valorTotal
                          }
                          pendenciasFinanceiras={data?.pendenciasFinanceiras}
                        />
                      </>
                    ) : (
                      <NoOcurrence />
                    )}
                  </div>
                  {spcSerasa &&
                    [
                      [
                        "Pendências Serasa",
                        pendenciasSerasa,
                        quantidadeSerasa,
                        spcSerasa?.resumoConsulta?.serasa?.valorTotal,
                      ],
                      [
                        "Pendências SPC",
                        pendenciasSpc,
                        quantidadeSpc,
                        spcSerasa?.resumoConsulta?.spc?.valorTotal,
                      ],
                    ].map(([titulo, pendencias, quantidade, valorTotal]) => (
                      <div key={titulo}>
                        <TitleStripe className="mb-4">{titulo}</TitleStripe>
                        {pendencias.length > 0 ? (
                          <PendenciasFinanceiras
                            quantidadeTotal={quantidade}
                            valorTotal={valorTotal}
                            pendenciasFinanceiras={pendencias}
                            temDataDisponibilidade={false}
                          />
                        ) : quantidade > 0 ? (
                          <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm font-medium text-amber-900">
                            {quantidade} ocorrência(s) informada(s) sem
                            detalhamento.
                          </div>
                        ) : (
                          <NoOcurrence />
                        )}
                      </div>
                    ))}
                  <div>
                    <TitleStripe className="mb-5">Protestos</TitleStripe>
                    {data?.protestos?.length > 0 ? (
                      <Protestos
                        quantidadeTotal={
                          data?.resumoConsulta?.protestos?.quantidadeTotal
                        }
                        valorTotal={data?.resumoConsulta?.protestos?.valorTotal}
                        protestos={data?.protestos}
                      />
                    ) : (
                      <NoOcurrence />
                    )}
                  </div>

                  <div>
                    <TitleStripe className="mb-5">
                      Cheques sem fundo
                    </TitleStripe>
                    {data?.chequesSemFundo?.length > 0 ? (
                      <>
                        <ChequesSemFundos
                          quantidadeTotal={
                            data?.resumoConsulta?.chequesSemFundo
                              ?.quantidadeTotal
                          }
                          chequesSemFundos={data?.chequesSemFundo}
                        />
                      </>
                    ) : (
                      <NoOcurrence />
                    )}
                  </div>
                  <div>
                    <TitleStripe className="mb-5">Cheques sustados</TitleStripe>
                    {data?.chequesSustados?.length > 0 ? (
                      <>
                        <ChequesSustados
                          quantidadeTotal={
                            data?.resumoConsulta?.chequesSustados
                              ?.quantidadeTotal
                          }
                          chequesSustados={data?.chequesSustados}
                        />
                      </>
                    ) : (
                      <NoOcurrence />
                    )}
                  </div>

                  {!statusConsumidorContainsNao && (
                    <>
                      {data?.pagamentoPontual &&
                        data?.pagamentoPontual?.series?.length > 0 && (
                          <div>
                            <TitleStripe className="mb-4">
                              Pagamentos Pontuais
                            </TitleStripe>
                            {data?.pagamentoPontual ? (
                              <>
                                <div className="mt-3 grid gap-3">
                                  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                                    <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-500">
                                      Pagamentos pontuais por período (últimos
                                      12 meses)
                                    </div>
                                    <div className="overflow-x-auto">
                                      <table className="min-w-full">
                                        <thead>
                                          <tr>
                                            {data?.pagamentoPontual?.series?.map(
                                              (pt, idx) => (
                                                <th
                                                  key={idx}
                                                  className="px-2 py-2 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500"
                                                >
                                                  {formatMesOuPontuacao(pt.mes)}
                                                </th>
                                              )
                                            )}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          <tr className="border-t border-slate-100">
                                            {data?.pagamentoPontual?.series?.map(
                                              (pt, idx) => {
                                                const n = parseFloat(pt?.valor);
                                                return (
                                                  <td
                                                    key={idx}
                                                    className="px-2 py-3 text-center text-sm font-semibold text-slate-700 tabular-nums"
                                                  >
                                                    {!isNaN(n) ? `${n}%` : "–"}
                                                  </td>
                                                );
                                              }
                                            )}
                                          </tr>
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>

                                  <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-slate-500">
                                    <span className="inline-flex items-center gap-2">
                                      <span className="h-2 w-2 rounded-full bg-emerald-500" />
                                      100%: Pagamentos pontuais
                                    </span>
                                    <span className="text-slate-300">|</span>
                                    <span className="inline-flex items-center gap-2">
                                      <span className="h-2 w-2 rounded-full bg-rose-500" />
                                      0%: Pagamentos não pontuais
                                    </span>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <NoOcurrence />
                            )}
                          </div>
                        )}

                      {data?.pagamentoAtrasado &&
                        data?.pagamentoAtrasado?.atraso_medio_dias?.length >
                          0 && (
                          <div>
                            <TitleStripe className="mb-4">
                              Pagamentos em atrasos
                            </TitleStripe>

                            {data?.pagamentoAtrasado ? (
                              <>
                                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                                  <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-500">
                                    Pagamentos em atraso por período (últimos 12
                                    meses)
                                  </div>
                                  <div className="overflow-x-auto">
                                    <table className="min-w-full">
                                      <thead>
                                        <tr>
                                          <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                                            Tipo
                                          </th>
                                          {colunasAtraso.map((mes) => (
                                            <th
                                              key={mes ?? "pontuacao"}
                                              className="px-2 py-2 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500"
                                            >
                                              {formatMesOuPontuacao(mes)}
                                            </th>
                                          ))}
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-slate-100">
                                        {FAIXAS_ATRASO.map(([label, key], i) => {
                                          const series =
                                            data?.pagamentoAtrasado?.[key] ||
                                            [];
                                          if (
                                            !Array.isArray(series) ||
                                            series.length === 0
                                          )
                                            return null;

                                          return (
                                            <tr key={i}>
                                              <td className="px-3 py-3 text-left text-2xs font-medium text-slate-700">
                                                {label}
                                              </td>
                                              {colunasAtraso.map((mes) => {
                                                const pt = series.find(
                                                  (item) =>
                                                    (item?.mes || null) === mes
                                                );
                                                const n = parseFloat(pt?.valor);
                                                const isDias =
                                                  key === "atraso_medio_dias";
                                                return (
                                                  <td
                                                    key={mes ?? "pontuacao"}
                                                    className="px-2 py-3 text-center text-2xs font-semibold text-slate-700 tabular-nums"
                                                  >
                                                    {!isNaN(n)
                                                      ? isDias
                                                        ? `${n}d`
                                                        : `${n}%`
                                                      : "–"}
                                                  </td>
                                                );
                                              })}
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>

                                <div className="mt-3 flex flex-wrap items-center justify-center gap-3 text-xs text-slate-500">
                                  <span className="inline-flex items-center gap-2">
                                    <span className="h-2 w-2 rounded-full bg-rose-500" />
                                    100%: Pagamentos em atrasos
                                  </span>
                                  <span className="text-slate-300">|</span>
                                  <span className="inline-flex items-center gap-2">
                                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                                    0%: Pagamentos em dia
                                  </span>
                                </div>
                              </>
                            ) : (
                              <NoOcurrence />
                            )}
                          </div>
                        )}

                      {periodosOperacoes.length > 0 && (
                        <div>
                          <TitleStripe className="mb-4">
                            Compromissos e valor dos títulos
                          </TitleStripe>

                          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                            <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-500">
                              Total de obrigações financeiras por período
                              (últimos 12 meses)
                            </div>
                            <div className="overflow-x-auto">
                              <table className="min-w-full">
                                <thead>
                                  <tr>
                                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                                      Indicador
                                    </th>
                                    {periodosOperacoes.map((periodo) => (
                                      <th
                                        key={periodo}
                                        className="px-2 py-2 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500"
                                      >
                                        {formatPeriodoLabel(periodo)}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>

                                <tbody className="divide-y divide-slate-100">
                                  <tr>
                                    <td className="px-3 py-3 text-left text-xs font-medium text-slate-700">
                                      Qtd. de títulos
                                    </td>
                                    {periodosOperacoes.map((periodo) => (
                                      <td
                                        key={`compromissos-${periodo}`}
                                        className="px-2 py-3 text-center text-xs font-semibold text-slate-700 tabular-nums"
                                      >
                                        {formatCompromissoValor(
                                          getCompromissosPorPeriodo(periodo)
                                            ?.valor
                                        )}
                                      </td>
                                    ))}
                                  </tr>
                                  <tr>
                                    <td className="px-3 py-3 text-left text-xs font-medium text-slate-700">
                                      Valor dos títulos
                                    </td>
                                    {periodosOperacoes.map((periodo) => (
                                      <td
                                        key={`credito-obtido-${periodo}`}
                                        className="px-2 py-3 text-center text-2xs font-semibold text-slate-700 tabular-nums"
                                      >
                                        {formatCreditoObtidoValor(
                                          getValorTitulosPorPeriodo(periodo)
                                        )}
                                      </td>
                                    ))}
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>

                          <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                            Descrição: Número total de obrigações financeiras
                            registradas no nome do consumidor ou empresa, como
                            boletos, duplicatas, carnês ou contratos de crédito.
                            Indica a quantidade de compromissos ativos e o total
                            de crédito obtido.
                          </div>
                        </div>
                      )}

                      {temComprometimentoFuturo && (
                        <div>
                          <TitleStripe className="mb-4">
                            Comprometimento Futuro
                          </TitleStripe>

                          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                            <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-500">
                              Comprometimento Futuro por prazo de vencimento
                            </div>
                            <div className="overflow-x-auto">
                              <table className="min-w-full">
                                <thead>
                                  <tr>
                                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                                      Indicador
                                    </th>
                                    {periodosSeries.map((periodo) => (
                                      <th
                                        key={periodo}
                                        className="px-2 py-2 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500"
                                      >
                                        {formatPeriodoLabel(periodo)}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>

                                <tbody className="divide-y divide-slate-100">
                                  <tr>
                                    <td className="px-3 py-3 text-left text-xs font-medium text-slate-700">
                                      Qtd. de títulos
                                    </td>
                                    {periodosSeries.map((periodo) => (
                                      <td
                                        key={`compromisso-${periodo}`}
                                        className="px-2 py-3 text-center text-xs font-semibold text-slate-700 tabular-nums"
                                      >
                                        {formatCompromissoValor(
                                          getCompromissoSerie(periodo)
                                            ?.pontuacao
                                        )}
                                      </td>
                                    ))}
                                  </tr>
                                  <tr>
                                    <td className="px-3 py-3 text-left text-xs font-medium text-slate-700">
                                      Valor dos títulos
                                    </td>
                                    {periodosSeries.map((periodo) => (
                                      <td
                                        key={`credito-${periodo}`}
                                        className="px-2 py-3 text-center text-xs font-semibold text-slate-700 tabular-nums"
                                      >
                                        {formatCreditoValor(
                                          getCreditoSerie(periodo)?.pontuacao
                                        )}
                                      </td>
                                    ))}
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {compromissosSeries.length > 0 && (
                            <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                              Descrição: Número total de obrigações financeiras
                              registradas no nome do consumidor ou empresa, como
                              boletos, duplicatas, carnês ou contratos de
                              crédito. Indica a quantidade de compromissos
                              ativos.
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  <div>
                    <TitleStripe className="mb-5">
                      Histórico de consultas
                    </TitleStripe>

                    {data?.historicoConsultas?.length > 0 ? (
                      <div className="flex flex-col gap-2">
                        <div className="flex-1">
                          <HistoricoConsultas
                            historicoConsultas={data?.historicoConsultas?.slice(
                              0,
                              5
                            )}
                          />
                        </div>
                        {/* {data?.historicoConsultasResumo
                          ?.paresMesAnoHistoricoConsultas?.length > 0 && (
                          <ReactApexChart
                            className="border border-gray-200 rounded-md mt-3"
                            options={{
                              chart: {
                                height: 150,
                                type: "bar",
                                toolbar: {
                                  show: false, // Remove o menu de ações
                                },
                              },
                              plotOptions: {
                                bar: {
                                  borderRadius: 2,
                                  dataLabels: {
                                    position: "center", // top, center, bottom
                                  },
                                },
                              },
                              dataLabels: {
                                enabled: true,
                                formatter: function (val) {
                                  return val;
                                },
                                offsetY: 0,
                                style: {
                                  fontSize: "12px",
                                  colors: ["white"],
                                },
                              },

                              xaxis: {
                                categories:
                                  data?.historicoConsultasResumo
                                    ?.paresMesAnoHistoricoConsultas,
                                position: "top",
                                axisBorder: {
                                  show: false,
                                },
                                axisTicks: {
                                  show: false,
                                },
                                crosshairs: {
                                  fill: {
                                    type: "gradient",
                                    gradient: {
                                      colorFrom: "#309ADB",
                                      colorTo: "#309ADB",
                                      stops: [0, 100],
                                      opacityFrom: 0.4,
                                      opacityTo: 0.5,
                                    },
                                  },
                                },
                                tooltip: {
                                  enabled: true,
                                },
                              },
                              yaxis: {
                                axisBorder: {
                                  show: false,
                                },
                                axisTicks: {
                                  show: false,
                                },
                                labels: {
                                  show: false,
                                  formatter: function (val) {
                                    return val;
                                  },
                                },
                              },
                              title: {
                                text: "Últimos 12 meses",
                                floating: true,
                                offsetY: 200,
                                align: "center",
                                style: {
                                  color: "#444",
                                },
                              },
                            }}
                            series={[
                              {
                                name: "Qtd. Consultas no período",
                                data: data?.historicoConsultasResumo
                                  ?.totaisHistoricoConsultas,
                              },
                            ]}
                            type="bar"
                            height={220}
                          />
                        )} */}
                      </div>
                    ) : (
                      <NoOcurrence />
                    )}
                  </div>

                  {/* <div>
                    <TitleStripe className="mb-3">
                      Histórico de endereços
                    </TitleStripe>
                    {data?.dadosCadastrais?.enderecos?.length > 0 ? (
                      <>
                        <div className="overflow-x-hidden shadow-sm rounded-lg mt-5 text-sm">
                          <Table striped>
                            <Table.Head>
                              <Table.HeadCell className="bg-gray-300 p-2">
                                Endereço
                              </Table.HeadCell>
                              <Table.HeadCell className="text-center bg-gray-300 p-2">
                                Bairro
                              </Table.HeadCell>
                              <Table.HeadCell className="text-center bg-gray-300 p-2">
                                Cidade:
                              </Table.HeadCell>
                              <Table.HeadCell className="text-center bg-gray-300 p-2">
                                CEP:
                              </Table.HeadCell>
                              <Table.HeadCell className="text-center bg-gray-300 p-2">
                                Prioridade:
                              </Table.HeadCell>
                            </Table.Head>

                            <Table.Body className="divide-y">
                              {data?.dadosCadastrais?.enderecos?.map(
                                (endereco, index) => (
                                  <Table.Row
                                    key={index}
                                    className="bg-white dark:border-gray-700 dark:bg-gray-800"
                                  >
                                    <Table.Cell className="font-medium text-gray-900 dark:text-white p-2">
                                      {endereco.endereco} Nº {endereco.numero}
                                    </Table.Cell>
                                    <Table.Cell className="text-center p-2">
                                      {endereco.bairro}
                                    </Table.Cell>
                                    <Table.Cell className="text-center p-2">
                                      {endereco.cidade}
                                    </Table.Cell>
                                    <Table.Cell className="text-center p-2">
                                      {endereco.cep}
                                    </Table.Cell>
                                    <Table.Cell className="text-center p-2">
                                      {endereco.prioridade}
                                    </Table.Cell>
                                  </Table.Row>
                                )
                              )}
                            </Table.Body>
                          </Table>
                        </div>
                      </>
                    ) : (
                      <NoOcurrence />
                    )}
                  </div>
                  <div>
                    <TitleStripe className="mb-3">
                      Histórico de emails
                    </TitleStripe>
                    {data?.dadosCadastrais?.emails?.length > 0 ? (
                      <>
                        <div className="overflow-x-hidden shadow-sm rounded-lg mt-5 text-sm">
                          <Table striped>
                            <Table.Head>
                              <Table.HeadCell className="bg-gray-300 p-2">
                                Email
                              </Table.HeadCell>
                              <Table.HeadCell className="text-center bg-gray-300 p-2">
                                Prioridade
                              </Table.HeadCell>
                            </Table.Head>

                            <Table.Body className="divide-y">
                              {data?.dadosCadastrais?.emails?.map(
                                (email, index) => (
                                  <Table.Row
                                    key={index}
                                    className="bg-white dark:border-gray-700 dark:bg-gray-800"
                                  >
                                    <Table.Cell className="font-medium text-gray-900 dark:text-white p-2">
                                      {email.email}
                                    </Table.Cell>
                                    <Table.Cell className="text-center p-2">
                                      {email.prioridade}
                                    </Table.Cell>
                                  </Table.Row>
                                )
                              )}
                            </Table.Body>
                          </Table>
                        </div>
                      </>
                    ) : (
                      <NoOcurrence />
                    )}
                  </div>

                  <div>
                    <TitleStripe className="mb-3">
                      Histórico de telefones
                    </TitleStripe>
                    {data?.dadosCadastrais?.telefones?.length > 0 ? (
                      <>
                        <div className="overflow-x-hidden shadow-sm rounded-lg mt-5 text-sm">
                          <Table striped>
                            <Table.Head>
                              <Table.HeadCell className="bg-gray-300 p-2 text-center">
                                DDD
                              </Table.HeadCell>
                              <Table.HeadCell className="text-center bg-gray-300 p-2">
                                Número
                              </Table.HeadCell>
                              <Table.HeadCell className="text-center bg-gray-300 p-2">
                                Prioridade:
                              </Table.HeadCell>
                            </Table.Head>

                            <Table.Body className="divide-y">
                              {data?.dadosCadastrais?.telefones?.map(
                                (telefone, index) => (
                                  <Table.Row
                                    key={index}
                                    className="bg-white dark:border-gray-700 dark:bg-gray-800"
                                  >
                                    <Table.Cell className="font-medium text-gray-900 dark:text-white p-2 text-center">
                                      {telefone.ddd}
                                    </Table.Cell>
                                    <Table.Cell className="text-center p-2">
                                      {telefone.numero}
                                    </Table.Cell>
                                    <Table.Cell className="text-center p-2">
                                      {telefone.prioridade}
                                    </Table.Cell>
                                  </Table.Row>
                                )
                              )}
                            </Table.Body>
                          </Table>
                        </div>
                      </>
                    ) : (
                      <NoOcurrence />
                    )}
                  </div>

                  <div>
                    <TitleStripe className="mb-3">
                      Histórico de celulares
                    </TitleStripe>
                    {data?.dadosCadastrais?.celulares?.length > 0 ? (
                      <>
                        <div className="overflow-x-hidden shadow-sm rounded-lg mt-5 text-sm">
                          <Table striped>
                            <Table.Head>
                              <Table.HeadCell className="bg-gray-300 p-2 text-center">
                                DDD
                              </Table.HeadCell>
                              <Table.HeadCell className="text-center bg-gray-300 p-2">
                                Número
                              </Table.HeadCell>
                              <Table.HeadCell className="text-center bg-gray-300 p-2">
                                Prioridade:
                              </Table.HeadCell>
                            </Table.Head>

                            <Table.Body className="divide-y">
                              {data?.dadosCadastrais?.celulares?.map(
                                (telefone, index) => (
                                  <Table.Row
                                    key={index}
                                    className="bg-white dark:border-gray-700 dark:bg-gray-800"
                                  >
                                    <Table.Cell className="font-medium text-gray-900 dark:text-white p-2 text-center">
                                      {telefone.ddd}
                                    </Table.Cell>
                                    <Table.Cell className="text-center p-2">
                                      {telefone.numero}
                                    </Table.Cell>
                                    <Table.Cell className="text-center p-2">
                                      {telefone.prioridade}
                                    </Table.Cell>
                                  </Table.Row>
                                )
                              )}
                            </Table.Body>
                          </Table>
                        </div>
                      </>
                    ) : (
                      <NoOcurrence />
                    )}
                  </div> */}

                  <FooterQuery hasHeader={hasHeaderPDF} />
                </Card>
              </>
            ) : (
              <QueryLoading />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PositivoPlusCNPJQuery;
